import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Star, Search, Users, CheckCircle2, 
  XCircle, Clock, AlertCircle, Save, Calendar, 
  BookOpen, PlusCircle, Trash2, Edit3, TrendingUp,
  ArrowLeft, ChevronRight, Info, Filter, MoreVertical
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { useToast } from '../context/ToastContext';
import { Topbar } from '../components/Topbar';
import { Card, Button, Badge, Skeleton, Input } from '../components/ui';
import { motion, AnimatePresence } from 'framer-motion';

export function ProfessorGradesPage() {
  const { user } = useAuth();
  const { 
    allEnrollments, 
    grades, 
    updateGrade, 
    deleteGradesByAssessment,
    assessments,
    setAssessments
  } = useData();
  const { addToast } = useToast();
  const loading = useLoading(500);

  // States
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [studentGrades, setStudentGrades] = useState({});
  const [showAddAssessment, setShowAddAssessment] = useState(false);
  const [assessmentModalMode, setAssessmentModalMode] = useState('create');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [newAssessment, setNewAssessment] = useState({
    name: '',
    type: 'Prova',
    weight: '40',
    date: '',
  });

  if (!user || user.type !== 'professor') return (
    <div className="flex items-center justify-center min-h-screen">
      <Card className="p-10 text-center">
        <AlertCircle size={48} className="text-red mx-auto mb-4" />
        <h2 className="text-xl font-bold">Acesso Negado</h2>
        <p className="text-muted">Não foi possível carregar as informações de notas.</p>
        <Button className="mt-6" onClick={() => window.location.href = '/'}>Voltar ao Início</Button>
      </Card>
    </div>
  );

  // Filtrar cursos do professor logado
  const profCourses = user?.courses ? user.courses.split(',').map(c => c.trim()).filter(c => c !== '') : [];
  
  const myClasses = [];
  profCourses.forEach(courseName => {
    const studentsA = (allEnrollments || []).filter(e => (e.course === courseName || e.courseName === courseName) && e.turma === 'A');
    const studentsB = (allEnrollments || []).filter(e => (e.course === courseName || e.courseName === courseName) && e.turma === 'B');
    
    myClasses.push({ id: `${courseName}-A`, course: courseName, turma: 'A', count: studentsA.length });
    myClasses.push({ id: `${courseName}-B`, course: courseName, turma: 'B', count: studentsB.length });
  });

  const studentsInSelectedClass = selectedClass ? (allEnrollments || []).filter(e => 
    (e.course === selectedClass.course || e.courseName === selectedClass.course) && e.turma === selectedClass.turma
  ).filter(s => s.fullName.toLowerCase().includes(searchTerm.toLowerCase())) : [];
  const classAssessments = selectedClass
    ? assessments.filter(a =>
        (!a.course || a.course === selectedClass.course) &&
        (!a.turma || a.turma === selectedClass.turma)
      )
    : assessments;

  // Carregar notas existentes quando mudar de turma/avaliação
  useEffect(() => {
    if (selectedClass && selectedAssessment && grades) {
      const currentGrades = {};
      studentsInSelectedClass.forEach(student => {
        const record = grades.find(g => 
          g.enrollmentId === student.enrollmentId && 
          g.subject === selectedClass.course && 
          g.assessmentId === selectedAssessment.id
        );
        if (record) {
          currentGrades[student.enrollmentId] = record.grade.toString();
        }
      });
      setStudentGrades(currentGrades);
    } else {
      setStudentGrades({});
    }
  }, [selectedClass, selectedAssessment, grades]);

  const handleGradeChange = (studentId, value) => {
    // Validar se é número entre 0 e 10
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 10)) {
      setStudentGrades(prev => ({ ...prev, [studentId]: value }));
    }
  };

  const handleSave = () => {
    if (!selectedAssessment) {
      addToast('Selecione uma avaliação antes de salvar.', 'warning');
      return;
    }

    const entries = Object.entries(studentGrades);
    if (entries.length === 0) {
      addToast('Nenhuma nota informada para salvar.', 'warning');
      return;
    }

    entries.forEach(([studentId, grade]) => {
      if (grade !== '') {
        updateGrade(studentId, selectedClass.course, selectedAssessment.id, parseFloat(grade), selectedAssessment.name);
      }
    });

    addToast(`Notas da ${selectedAssessment.name} salvas com sucesso!`, 'success');
  };

  const getClassAverage = () => {
    if (!selectedAssessment || studentsInSelectedClass.length === 0) return 0;
    const values = Object.values(studentGrades).map(v => parseFloat(v)).filter(v => !isNaN(v));
    if (values.length === 0) return 0;
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const stats = {
    approved: Object.values(studentGrades).filter(v => parseFloat(v) >= 5).length,
    failed: Object.values(studentGrades).filter(v => parseFloat(v) < 5 && v !== '').length,
    total: studentsInSelectedClass.length
  };

  const handleCreateAssessment = () => {
    const name = newAssessment.name.trim();
    const weight = parseFloat(newAssessment.weight);

    if (!name) {
      addToast('Informe o nome da avaliação.', 'warning');
      return;
    }

    if (!newAssessment.date) {
      addToast('Informe a data da avaliação.', 'warning');
      return;
    }

    if (Number.isNaN(weight) || weight <= 0 || weight > 100) {
      addToast('O peso deve estar entre 1 e 100.', 'warning');
      return;
    }

    if (assessmentModalMode === 'edit' && selectedAssessment) {
      const updated = {
        ...selectedAssessment,
        name,
        type: newAssessment.type,
        weight,
        date: newAssessment.date,
      };
      setAssessments(prev => prev.map(item => item.id === selectedAssessment.id ? updated : item));
      setSelectedAssessment(updated);
      setShowAddAssessment(false);
      setNewAssessment({
        name: '',
        type: 'Prova',
        weight: '40',
        date: '',
      });
      addToast('Avaliação atualizada com sucesso!', 'success');
      return;
    }

    const created = {
      id: Date.now(),
      name,
      type: newAssessment.type,
      weight,
      date: newAssessment.date,
      course: selectedClass?.course || null,
      turma: selectedClass?.turma || null,
      max: 10
    };

    setAssessments(prev => [created, ...prev]);
    setSelectedAssessment(created);
    setShowAddAssessment(false);
    setNewAssessment({
      name: '',
      type: 'Prova',
      weight: '40',
      date: '',
    });
    addToast('Nova avaliação criada com sucesso!', 'success');
  };

  const openCreateAssessmentModal = () => {
    setAssessmentModalMode('create');
    setNewAssessment({
      name: '',
      type: 'Prova',
      weight: '40',
      date: '',
    });
    setShowAddAssessment(true);
  };

  const openEditAssessmentModal = (assessment) => {
    setAssessmentModalMode('edit');
    setSelectedAssessment(assessment);
    setNewAssessment({
      name: assessment.name || '',
      type: assessment.type || 'Prova',
      weight: String(assessment.weight ?? '40'),
      date: assessment.date || '',
    });
    setShowAddAssessment(true);
  };

  const handleDeleteAssessment = (assessmentId) => {
    const target = assessments.find(item => item.id === assessmentId);
    if (!target) return;
    const confirmed = window.confirm(`Deseja excluir a avaliação "${target.name}"?`);
    if (!confirmed) return;

    setAssessments(prev => prev.filter(item => item.id !== assessmentId));
    if (selectedAssessment?.id === assessmentId) {
      setSelectedAssessment(null);
    }
    deleteGradesByAssessment(target.course || selectedClass?.course, assessmentId);
    addToast(`Avaliação "${target.name}" excluída.`, 'success');
  };

  return (
    <div className="min-h-screen pb-20">
      <Topbar />
      
      <div className="max-w-7xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          {!selectedClass ? (
            <motion.div 
              key="class-list"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-10"
            >
              <div>
                <h2 className="text-4xl font-black tracking-tight uppercase">Gestão de Notas</h2>
                <p className="text-muted font-bold text-sm uppercase tracking-widest mt-2">Selecione uma turma para lançar notas e gerenciar avaliações.</p>
              </div>

              {myClasses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {myClasses.map((cls) => (
                    <button 
                      key={cls.id}
                      onClick={() => setSelectedClass(cls)}
                      className={`group text-left border border-white/10 p-7 rounded-[2rem] transition-all relative overflow-hidden hover:border-purple/30 hover:shadow-[0_25px_70px_rgba(0,0,0,0.45)] ${
                        cls.turma === 'A'
                          ? 'bg-gradient-to-br from-purple/10 via-[#0a1120] to-[#0a1120]'
                          : 'bg-gradient-to-br from-blue/10 via-[#0a1120] to-[#0a1120]'
                      }`}
                    >
                      <div
                        className={`absolute top-0 right-0 w-44 h-44 blur-[70px] -mr-24 -mt-24 transition-all ${
                          cls.turma === 'A' ? 'bg-purple/20 group-hover:bg-purple/30' : 'bg-blue/20 group-hover:bg-blue/30'
                        }`}
                      />
                      <div className="relative flex items-center justify-between gap-6">
                        <div className="min-w-0">
                          <div className="flex items-center gap-3">
                            <Badge
                              variant="purple"
                              className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                cls.turma === 'A' ? '' : 'bg-blue/20 text-blue border-blue/30'
                              }`}
                            >
                              Turma {cls.turma}
                            </Badge>
                            <span className="text-[10px] font-black uppercase tracking-[3px] text-muted">Notas</span>
                          </div>
                          <h3 className="mt-4 text-2xl font-black text-white uppercase leading-tight truncate">{cls.course}</h3>
                          <div className="mt-3 flex items-center gap-2 text-muted">
                            <Users size={16} />
                            <span className="text-xs font-bold uppercase tracking-widest">{cls.count} alunos</span>
                          </div>
                        </div>

                        <div className="shrink-0 flex items-center gap-3">
                          <span className="hidden sm:inline text-[10px] font-black uppercase tracking-[3px] text-white/60">
                            Abrir
                          </span>
                          <div
                            className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all border ${
                              cls.turma === 'A'
                                ? 'bg-purple/10 border-purple/20 text-purple group-hover:bg-purple group-hover:text-white group-hover:border-purple'
                                : 'bg-blue/10 border-blue/20 text-blue group-hover:bg-blue group-hover:text-white group-hover:border-blue'
                            }`}
                          >
                            <ChevronRight size={20} />
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20 bg-white/5 rounded-[3rem] border-2 border-dashed border-white/10">
                  <BookOpen size={48} className="mx-auto mb-4 opacity-20" />
                  <h3 className="text-xl font-black">Nenhuma turma vinculada.</h3>
                  <p className="text-muted mt-2">Contate a coordenação acadêmica.</p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div 
              key="grades-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              {/* Header de Turma */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex items-center gap-6">
                  <button 
                    onClick={() => { setSelectedClass(null); setSelectedAssessment(null); }}
                    className="w-14 h-14 bg-white/5 rounded-2xl flex items-center justify-center hover:bg-white/10 transition-all text-white"
                  >
                    <ArrowLeft size={24} />
                  </button>
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-3xl font-black uppercase tracking-tight">{selectedClass.course}</h2>
                      <Badge variant="purple">Turma {selectedClass.turma}</Badge>
                    </div>
                    <p className="text-muted text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                      <TrendingUp size={14} /> Média da Turma: <span className="text-white">{getClassAverage()}</span>
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 w-full md:w-auto">
                  <Button 
                    variant="outline"
                    className="flex-1 md:flex-none py-4 px-6 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-2"
                    onClick={openCreateAssessmentModal}
                  >
                    <PlusCircle size={18} /> Nova Avaliação
                  </Button>
                  <Button 
                    className="flex-1 md:flex-none py-4 px-10 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 bg-purple hover:bg-purple/80 text-white shadow-xl shadow-purple/20"
                    onClick={handleSave}
                  >
                    <Save size={18} /> Salvar Notas
                  </Button>
                </div>
              </div>

              {/* Seletor de Avaliação */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {classAssessments.map(as => (
                  <button 
                    key={as.id}
                    onClick={() => setSelectedAssessment(as)}
                    className={`text-left p-6 rounded-[2rem] border transition-all relative overflow-hidden ${
                      selectedAssessment?.id === as.id 
                      ? 'bg-purple/10 border-purple/40 ring-1 ring-purple/40' 
                      : 'bg-white/5 border-white/5 hover:border-white/10'
                    }`}
                  >
                    <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditAssessmentModal(as);
                        }}
                        className="w-9 h-9 rounded-xl border border-white/10 bg-white/5 text-muted hover:text-white hover:border-purple/30 hover:bg-purple/10 transition-all flex items-center justify-center"
                        title="Editar avaliação"
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAssessment(as.id);
                        }}
                        className="w-9 h-9 rounded-xl border border-white/10 bg-white/5 text-muted hover:text-red hover:border-red/30 hover:bg-red/10 transition-all flex items-center justify-center"
                        title="Excluir avaliação"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="flex justify-between items-start mb-4">
                      <Badge variant={selectedAssessment?.id === as.id ? 'purple' : 'outline'} className="text-[9px] uppercase font-black">
                        {as.type}
                      </Badge>
                      <span className="text-[10px] font-bold text-muted uppercase">{as.weight}% Peso</span>
                    </div>
                    <h4 className="text-lg font-black uppercase mb-1">{as.name}</h4>
                    <p className="text-[10px] text-muted font-bold uppercase tracking-widest">Data: {new Date(as.date).toLocaleDateString()}</p>
                    
                    {selectedAssessment?.id === as.id && (
                      <motion.div 
                        layoutId="active-assessment"
                        className="absolute bottom-0 left-0 h-1 bg-purple w-full"
                      />
                    )}
                  </button>
                ))}
              </div>

              <Card className="overflow-hidden border-white/5 shadow-2xl">
                <div className="p-5 border-b border-white/5 bg-white/[0.01]">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="relative w-full max-w-xl">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted" size={18} />
                      <input 
                        type="text" 
                        placeholder="Buscar aluno por nome..."
                        className="w-full bg-white/5 border border-white/5 rounded-2xl py-3 pl-12 pr-4 text-sm outline-none focus:border-purple/40 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <span className="badge" style={{ background: 'rgba(139,92,246,0.12)', color: 'var(--purple)' }}>
                        {selectedAssessment ? selectedAssessment.name : 'Selecione uma avaliação'}
                      </span>
                      <span className="badge" style={{ background: 'rgba(255,255,255,0.05)', color: '#dbe3f2' }}>
                        Média {getClassAverage()}
                      </span>
                      <span className="badge" style={{ background: 'rgba(67,214,111,0.10)', color: 'var(--green)' }}>
                        Aprovados {stats.approved}
                      </span>
                      <span className="badge" style={{ background: 'rgba(255,77,109,0.10)', color: 'var(--red)' }}>
                        Reprovados {stats.failed}
                      </span>
                    </div>
                  </div>
                </div>

                {!selectedAssessment ? (
                  <div style={{ padding: 28, color: 'var(--muted)', fontWeight: 800 }}>
                    Selecione uma avaliação acima para lançar ou editar as notas da turma.
                  </div>
                ) : (
                  <div className="tableWrap">
                    {studentsInSelectedClass.length > 0 ? (
                      <table className="w-full">
                        <thead className="bg-white/5">
                          <tr>
                            <th className="px-6 py-4 text-left text-[10px] font-black uppercase tracking-[3px] text-muted">Estudante</th>
                            <th className="px-6 py-4 text-center text-[10px] font-black uppercase tracking-[3px] text-muted">Status</th>
                            <th className="px-6 py-4 text-right text-[10px] font-black uppercase tracking-[3px] text-muted w-40">Nota</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {studentsInSelectedClass.map(student => {
                            const gradeValue = parseFloat(studentGrades[student.enrollmentId]);
                            return (
                              <tr key={student.enrollmentId} className="hover:bg-white/[0.02] transition-colors">
                                <td className="px-6 py-5">
                                  <div className="flex items-center gap-4">
                                    <div className="w-11 h-11 rounded-2xl bg-purple/10 flex items-center justify-center text-purple font-black text-base shadow-inner">
                                      {student.fullName[0]}
                                    </div>
                                    <div>
                                      <strong className="text-sm block text-white uppercase">{student.fullName}</strong>
                                      <span className="text-[11px] text-muted font-bold">{student.enrollmentId}</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-5 text-center">
                                  <Badge 
                                    variant={gradeValue >= 5 ? 'green' : gradeValue >= 0 ? 'red' : 'outline'}
                                    className="px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest"
                                  >
                                    {gradeValue >= 5 ? 'Aprovado' : gradeValue >= 0 ? 'Reprovado' : 'Pendente'}
                                  </Badge>
                                </td>
                                <td className="px-6 py-5 text-right">
                                  <input 
                                    type="number" 
                                    step="0.1"
                                    min="0"
                                    max="10"
                                    placeholder="-"
                                    className="w-28 bg-[#0b1322] border border-white/15 rounded-xl px-3 py-3 text-white outline-none focus:border-purple font-mono font-black text-center text-xl shadow-inner"
                                    style={{
                                      MozAppearance: 'textfield',
                                      appearance: 'textfield',
                                      backgroundColor: '#111827',
                                      color: '#ffffff',
                                      WebkitTextFillColor: '#ffffff',
                                      caretColor: '#ffffff'
                                    }}
                                    value={studentGrades[student.enrollmentId] || ''}
                                    onChange={(e) => handleGradeChange(student.enrollmentId, e.target.value)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-20 text-center">
                        <Users size={40} className="mx-auto mb-4 opacity-10" />
                        <p className="text-muted font-bold uppercase tracking-widest">Nenhum aluno encontrado.</p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Modal Nova Avaliação */}
      {showAddAssessment &&
        createPortal(
          <div
            className="modalOverlay"
            style={{ zIndex: 2147483647 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowAddAssessment(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              className="modal"
              style={{ width: 'min(520px, calc(100vw - 36px))', padding: 22, borderRadius: 18 }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                    {assessmentModalMode === 'edit' ? 'Editar Avaliação' : 'Nova Avaliação'}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 900, marginTop: 4, color: 'white' }}>
                    {assessmentModalMode === 'edit' ? 'Atualizar' : 'Configurar'}
                  </div>
                </div>
                <button
                  onClick={() => setShowAddAssessment(false)}
                  className="manage outline"
                  style={{ width: 40, height: 40, padding: 0, borderRadius: 12, display: 'grid', placeItems: 'center' }}
                >
                  <XCircle size={22} />
                </button>
              </div>

              <div style={{ display: 'grid', gap: 12 }}>
                <div className="formGroup" style={{ marginBottom: 0 }}>
                  <label>Nome da Avaliação</label>
                  <input
                    type="text"
                    placeholder="Ex: Avaliação Bimestral"
                    value={newAssessment.name}
                    onChange={(e) => setNewAssessment(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="formGroup" style={{ marginBottom: 0 }}>
                    <label>Tipo</label>
                    <select
                      value={newAssessment.type}
                      onChange={(e) => setNewAssessment(prev => ({ ...prev, type: e.target.value }))}
                    >
                      <option>Prova</option>
                      <option>Trabalho</option>
                      <option>Projeto</option>
                      <option>Seminário</option>
                    </select>
                  </div>
                  <div className="formGroup" style={{ marginBottom: 0 }}>
                    <label>Peso (%)</label>
                    <input
                      type="number"
                      placeholder="40"
                      min="1"
                      max="100"
                      value={newAssessment.weight}
                      onChange={(e) => setNewAssessment(prev => ({ ...prev, weight: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="formGroup" style={{ marginBottom: 0 }}>
                  <label>Data Prevista</label>
                  <input
                    type="date"
                    value={newAssessment.date}
                    onChange={(e) => setNewAssessment(prev => ({ ...prev, date: e.target.value }))}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <Button variant="outline" className="flex-1" onClick={() => setShowAddAssessment(false)}>
                  Cancelar
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCreateAssessment}
                >
                  {assessmentModalMode === 'edit' ? 'Salvar Alterações' : 'Criar Avaliação'}
                </Button>
              </div>
            </motion.div>
          </div>,
          document.body
        )}
    </div>
  );
}

function SummaryItem({ label, value, color }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[10px] font-black uppercase tracking-widest opacity-60">{label}</span>
      <strong className={`text-lg font-black ${color}`}>{value}</strong>
    </div>
  );
}
